import { useGSAP } from '@gsap/react';
import { gsap } from 'gsap';
import { useRef } from 'react';

import { clamp } from '@/core/math';
import { useContent } from '@/content/ContentContext';
import { ExpertiseVisual } from '@/features/expertises/ExpertiseVisual';
import { Chapter, ChapterColumn } from '@/features/journey/Chapter';
import { WIDE_WITH_MOTION } from '@/motion/queries';
import { cx } from '@/ui/classNames';
import { Container } from '@/ui/Layout/Container';
import { RevealText } from '@/ui/Reveal/RevealText';
import { typography } from '@/ui/Typography/typography';

import styles from './Expertises.module.css';

/** Inclinaison et recul maximaux d'un panneau au bord de l'écran. */
const ORBIT = { tilt: 24, depth: 220 } as const;

/**
 * Chapitre 03, les quatre savoir-faire, sous le trou noir « Indigo ». Sur grand
 * écran, le défilement fait passer les panneaux en arc devant le trou noir,
 * comme en orbite ; ailleurs, ils s'empilent simplement.
 */
export function Expertises() {
  const { expertises, mobile } = useContent();
  const sectionRef = useRef<HTMLElement>(null);
  const pinRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);

  useGSAP(
    () => {
      const section = sectionRef.current;
      const pin = pinRef.current;
      const track = trackRef.current;
      if (!section || !pin || !track) return;

      const media = gsap.matchMedia();
      media.add(WIDE_WITH_MOTION, () => {
        section.setAttribute('data-horizontal', '');
        const distance = (): number => Math.max(track.scrollWidth - window.innerWidth, 0);

        // Chaque panneau pivote selon sa distance au centre de l'écran : un carrousel en orbite.
        const panels = gsap.utils.toArray<HTMLElement>('[data-panel]', track);
        const orbit = (): void => {
          const center = window.innerWidth / 2;
          for (const panel of panels) {
            const bounds = panel.getBoundingClientRect();
            const offset = clamp(
              (bounds.left + bounds.width / 2 - center) / window.innerWidth,
              -1,
              1,
            );
            gsap.set(panel, { rotateY: -offset * ORBIT.tilt, z: -Math.abs(offset) * ORBIT.depth });
          }
        };

        const pan = gsap.to(track, {
          x: () => -distance(),
          ease: 'none',
          onUpdate: orbit,
          scrollTrigger: {
            trigger: pin,
            start: 'top top',
            end: () => `+=${distance()}`,
            pin: true,
            scrub: 1,
            invalidateOnRefresh: true,
          },
        });

        // Légère parallaxe des images à l'intérieur de leur cadre.
        gsap.utils.toArray<HTMLElement>('[data-parallax]', track).forEach((image) => {
          gsap.fromTo(
            image,
            { xPercent: -6, scale: 1.12 },
            {
              xPercent: 6,
              ease: 'none',
              scrollTrigger: {
                trigger: image,
                containerAnimation: pan,
                start: 'left right',
                end: 'right left',
                scrub: true,
              },
            },
          );
        });

        orbit();
        return () => {
          section.removeAttribute('data-horizontal');
          gsap.set(panels, { clearProps: 'transform' });
        };
      });
      return () => {
        media.revert();
      };
    },
    { scope: sectionRef },
  );

  return (
    <Chapter
      ref={sectionRef}
      chapter="expertises"
      id="expertises"
      className={styles.section}
      aria-labelledby="expertises-title"
    >
      <div ref={pinRef} className={styles.pin}>
        <Container>
          <ChapterColumn chapter="expertises" className={styles.header}>
            <RevealText
              id="expertises-title"
              className={cx(typography.heading, typography.h2, styles.title)}
            >
              {expertises.title}
            </RevealText>
            <p className={typography.lead}>{expertises.lead}</p>
          </ChapterColumn>
        </Container>

        <div ref={trackRef} className={styles.track}>
          {expertises.items.map((item) => (
            <article
              key={item.index}
              className={styles.panel}
              data-panel
              aria-labelledby={`expertise-${item.index}`}
            >
              <div className={styles.text}>
                <span className={styles.index} aria-hidden="true">
                  {item.index}
                </span>
                <h3 id={`expertise-${item.index}`} className={styles.panelTitle}>
                  {item.title}
                </h3>
                <p className={styles.description}>{item.description}</p>
                <ul className={styles.skills}>
                  {item.skills.map((skill) => (
                    <li key={skill}>{skill}</li>
                  ))}
                </ul>
              </div>
              <ExpertiseVisual expertise={item} app={mobile.apps.perfume} />
            </article>
          ))}
        </div>
      </div>
    </Chapter>
  );
}
